import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div
        className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const Field = ({ label, value, badge, badgeClass }) => (
    <div className="col-md-3 mb-3">
        <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>{label}</div>
        {badge
            ? <span className={`badge ${badgeClass || "bg-secondary"}`} style={{ fontSize: 13 }}>{value || "—"}</span>
            : <div style={{ fontSize: 14, color: "#111827", fontWeight: 500 }}>{value || <span style={{ color: "#9ca3af" }}>—</span>}</div>
        }
    </div>
);

const AuditSection = ({ count, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-3 border">
            <div className="fw-bold p-2 border-bottom d-flex align-items-center justify-content-between"
                style={{ backgroundColor: "#DFE4EB", cursor: "pointer", userSelect: "none" }}
                onClick={() => setOpen(o => !o)}>
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-clock-history"></i> Audit Trail
                    <span className="badge bg-secondary ms-1" style={{ fontSize: 11 }}>{count}</span>
                </span>
                <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"}`} style={{ fontSize: 13, color: "#6b7280" }}></i>
            </div>
            {open && <div className="p-3">{children}</div>}
        </div>
    );
};

const InfoSection = ({ title, icon, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-3 border">
            <div className="fw-bold p-2 border-bottom d-flex align-items-center justify-content-between"
                style={{ backgroundColor: "#DFE4EB", cursor: "pointer", userSelect: "none" }}
                onClick={() => setOpen(o => !o)}>
                <span className="d-flex align-items-center gap-2">
                    <i className={`bi ${icon}`}></i> {title}
                </span>
                <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"}`} style={{ fontSize: 13, color: "#6b7280" }}></i>
            </div>
            {open && <div className="row g-0 p-3">{children}</div>}
        </div>
    );
};

const DEPRECIATION_METHODS = ["Straight-Line", "WDV", "None"];
const STATUS_BADGE = { "Available": "bg-success", "Allocated": "bg-primary", "In Repair": "bg-warning text-dark", "Disposed": "bg-secondary", "In Warehouse": "bg-info text-dark" };
const STATUS_OPTIONS = Object.keys(STATUS_BADGE);
const LOCATIONS = ["Head Office", "Branch Office", "Remote", "Warehouse", "Client Site"];
const DEPARTMENTS = ["IT", "HR", "Finance", "Operations", "Sales", "Marketing", "Admin", "Engineering", "Legal"];

const EVENT_LABELS = {
    ASSET_CREATED:       "Asset Registered",
    ASSET_UPDATED:       "Details Updated",
    STATUS_CHANGED:      "Status Changed",
    ASSET_DELETED:       "Asset Deleted",
    ALLOCATED:           "Asset Allocated",
    RETURNED:            "Asset Returned",
    OTP_VERIFIED:        "Acknowledgment Confirmed",
    MAINTENANCE_RAISED:  "Maintenance Raised",
    MAINTENANCE_CLOSED:  "Maintenance Closed",
    TRANSFERRED:         "Asset Transferred",
};

const EVENT_CONFIG = {
    ASSET_CREATED:      { icon: "bi-plus-circle-fill",    color: "#16a34a", bg: "#dcfce7" },
    ASSET_UPDATED:      { icon: "bi-pencil-fill",         color: "#2563eb", bg: "#dbeafe" },
    STATUS_CHANGED:     { icon: "bi-arrow-left-right",    color: "#7c3aed", bg: "#ede9fe" },
    ASSET_DELETED:      { icon: "bi-trash-fill",          color: "#dc2626", bg: "#fee2e2" },
    ALLOCATED:          { icon: "bi-person-check-fill",   color: "#0891b2", bg: "#cffafe" },
    RETURNED:           { icon: "bi-box-arrow-in-left",   color: "#9ca3af", bg: "#f3f4f6" },
    OTP_VERIFIED:       { icon: "bi-patch-check-fill",    color: "#16a34a", bg: "#dcfce7" },
    MAINTENANCE_RAISED: { icon: "bi-tools",               color: "#d97706", bg: "#fef3c7" },
    MAINTENANCE_CLOSED: { icon: "bi-wrench-adjustable",   color: "#16a34a", bg: "#dcfce7" },
    TRANSFERRED:        { icon: "bi-arrow-repeat",        color: "#7c3aed", bg: "#ede9fe" },
};

const initialForm = {
  category: "",
  subCategory: "",
  assetName: "",
  brand: "",
  modelNumber: "",
  serialNumber: "",
  colour: "",
  purchaseDate: "",
  purchaseCost: "",
  vendorId: "",
  vendorName: "",
  poNumber: "",
  grnNumber: "",
  warrantyStartDate: "",
  warrantyEndDate: "",
  location: "",
  department: "",
  status: "Available",
  depreciationMethod: "Straight-Line",
  usefulLifeYears: "",
  residualValue: "",
  remarks: "",
};

export default function EditAsset() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState(initialForm);
  const [categoryFields, setCategoryFields] = useState({});
  const [vendors, setVendors] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("token");
  
  const [allocations, setAllocations] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [activeTab, setActiveTab] = useState("allocations");
  const [visitedTabs, setVisitedTabs] = useState(new Set(["allocations"]));
  const [navbarColor, setNavbarColor] = useState("#080235");
  const [assetDetails, setAssetDetails] = useState(null);

  // Dynamic master data from API
  const [categories, setCategories] = useState([]);
  const [dynamicSubCategories, setDynamicSubCategories] = useState([]);
  const [dynamicFields, setDynamicFields] = useState([]);

  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) {
      toast.error("You must be logged in.");
      navigate("/");
      return;
    }
    // if (!roles.includes("ROLE_ADMIN")) {
    //   toast.error("Unauthorized.");
    //   navigate("/");
    // }
  }, [navigate, token]);

  useEffect(() => {
      const userId = localStorage.getItem("userId");
      if (!token || !userId) return;
      fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [assetRes, vendorRes, allocRes, maintRes, trailRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/assets/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/vendors?status=Active`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/asset-allocations/asset/${id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_BASE_URL}/api/asset-maintenance/asset/${id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_BASE_URL}/api/assets/${id}/audit-trail`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);

        if (!assetRes.ok) throw new Error("Failed to load asset.");
        const asset = await assetRes.json();
        const vendorData = vendorRes.ok ? await vendorRes.json() : [];
        const vendorList = Array.isArray(vendorData) ? vendorData : vendorData.content || [];

        setVendors(vendorList);
        setAssetDetails(asset);
        
        if (allocRes && allocRes.ok) setAllocations(await allocRes.json());
        if (maintRes && maintRes.ok) setMaintenance(await maintRes.json());
        if (trailRes && trailRes.ok) setAuditTrail(await trailRes.json());

        setFormData({
          category: asset.category || "",
          subCategory: asset.subCategory || "",
          assetName: asset.assetName || "",
          brand: asset.brand || "",
          modelNumber: asset.modelNumber || "",
          serialNumber: asset.serialNumber || "",
          colour: asset.colour || "",
          purchaseDate: asset.purchaseDate || "",
          purchaseCost: asset.purchaseCost || "",
          vendorId: asset.vendorId || "",
          vendorName: asset.vendorName || "",
          poNumber: asset.poNumber || "",
          grnNumber: asset.grnNumber || "",
          warrantyStartDate: asset.warrantyStartDate || "",
          warrantyEndDate: asset.warrantyEndDate || "",
          location: asset.location || "",
          department: asset.department || "",
          status: asset.status || "Available",
          depreciationMethod: asset.depreciationMethod || "Straight-Line",
          usefulLifeYears: asset.usefulLifeYears || "",
          residualValue: asset.residualValue || "",
          remarks: asset.remarks || asset.handoverRemarks || "",
        });
        setCategoryFields(asset.categorySpecificDetails || asset.categorySpecific || {});
      } catch (err) {
        toast.error(err.message || "Failed to load asset.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, token]);

  // Fetch all categories on mount
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/asset-masters/categories`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, [token]);

  // Fetch subcategories when category or categories list changes
  useEffect(() => {
    if (!formData.category || categories.length === 0) return;
    const cat = categories.find((c) => c.name === formData.category);
    if (!cat) return;
    fetch(`${API_BASE_URL}/api/asset-masters/subcategories?categoryId=${cat.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setDynamicSubCategories(Array.isArray(data) ? data : []))
      .catch(() => setDynamicSubCategories([]));
  }, [formData.category, categories, token]);

  // Fetch dynamic fields when subcategory changes
  useEffect(() => {
    if (!formData.subCategory) { setDynamicFields([]); return; }
    fetch(
      `${API_BASE_URL}/api/asset-masters/fields/for-form?subCategoryName=${encodeURIComponent(formData.subCategory)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => r.json())
      .then((data) => setDynamicFields(Array.isArray(data) ? data : []))
      .catch(() => setDynamicFields([]));
  }, [formData.subCategory, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "category") {
      setFormData((prev) => ({ ...prev, category: value, subCategory: "" }));
      setCategoryFields({});
      return;
    }
    if (name === "vendorId") {
      const selectedVendor = vendors.find((vendor) => String(vendor.id) === value);
      setFormData((prev) => ({
        ...prev,
        vendorId: value,
        vendorName: selectedVendor?.name || prev.vendorName,
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.category) nextErrors.category = "Category is required.";
    if (!formData.subCategory) nextErrors.subCategory = "Sub-category is required.";
    if (!formData.assetName.trim()) nextErrors.assetName = "Asset name is required.";
    if (!formData.brand.trim()) nextErrors.brand = "Brand is required.";
    if (!formData.modelNumber.trim()) nextErrors.modelNumber = "Model number is required.";
    if (!formData.serialNumber.trim()) nextErrors.serialNumber = "Serial number is required.";
    if (!formData.purchaseDate) nextErrors.purchaseDate = "Purchase date is required.";
    if (!formData.purchaseCost || Number(formData.purchaseCost) <= 0) nextErrors.purchaseCost = "Purchase cost must be greater than 0.";
    if (!formData.warrantyStartDate) nextErrors.warrantyStartDate = "Warranty start date is required.";
    if (!formData.warrantyEndDate) nextErrors.warrantyEndDate = "Warranty end date is required.";
    dynamicFields.forEach((field) => {
      if (field.required && !String(categoryFields[field.name] || "").trim()) {
        nextErrors[`category_${field.name}`] = `${field.label} is required.`;
      }
    });
    return nextErrors;
  };

  const handleTabChange = (key) => {
      setActiveTab(key);
      setVisitedTabs(prev => new Set(prev).add(key));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fix the highlighted asset fields.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/assets/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          categorySpecificDetails: categoryFields,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Asset updated successfully.");
      navigate("/allassets");
    } catch (err) {
      toast.error(err.message || "Failed to update asset.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="fixed-top">
          <AssetModuleNavbar />
        </div>
        <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh", marginTop: 106 }}>
          <div className="spinner-border text-primary"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top">
        <AssetModuleNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
        <div className="mb-3">
          <h4 className="m-0">
            <i className="bi bi-pencil-square me-2"></i> Edit Asset
          </h4>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={submitting}>
                {submitting ? "Saving..." : "Save "}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                Cancel
              </button>
            </div>
            <div className="d-flex gap-3">
              <a href={`/viewasset/${id}`} className="text-primary text-decoration-none">View</a>
              <a href="/allassets" className="text-primary text-decoration-none">Asset List</a>
            </div>
          </div>
        </div>

        <Section title="Asset Classification">
          <div className="col-md-3">
            <label className="form-label">Category <span className="text-danger">*</span></label>
            <select name="category" className={`form-select ${errors.category ? "is-invalid" : ""}`} value={formData.category} onChange={handleChange}>
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Sub-category <span className="text-danger">*</span></label>
            <select name="subCategory" className={`form-select ${errors.subCategory ? "is-invalid" : ""}`} value={formData.subCategory} onChange={handleChange} disabled={!formData.category}>
              <option value="">Select sub-category</option>
              {dynamicSubCategories.map((sub) => (
                <option key={sub.id} value={sub.name}>{sub.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Asset Name <span className="text-danger">*</span></label>
            <input name="assetName" className={`form-control ${errors.assetName ? "is-invalid" : ""}`} value={formData.assetName} onChange={handleChange} />
          </div>
          <div className="col-md-2">
            <label className="form-label">Status</label>
            <select name="status" className="form-select" value={formData.status} onChange={handleChange}>
              {STATUS_OPTIONS.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </div>
        </Section>

        <Section title="Identification Details">
          <div className="col-md-3">
            <label className="form-label">Brand <span className="text-danger">*</span></label>
            <input name="brand" className={`form-control ${errors.brand ? "is-invalid" : ""}`} value={formData.brand} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Model Number <span className="text-danger">*</span></label>
            <input name="modelNumber" className={`form-control ${errors.modelNumber ? "is-invalid" : ""}`} value={formData.modelNumber} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Serial Number <span className="text-danger">*</span></label>
            <input name="serialNumber" className={`form-control ${errors.serialNumber ? "is-invalid" : ""}`} value={formData.serialNumber} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Colour</label>
            <input name="colour" className="form-control" value={formData.colour} onChange={handleChange} />
          </div>
        </Section>

        <Section title="Procurement & Warranty">
          <div className="col-md-3">
            <label className="form-label">Purchase Date <span className="text-danger">*</span></label>
            <input type="date" name="purchaseDate" className={`form-control ${errors.purchaseDate ? "is-invalid" : ""}`} value={formData.purchaseDate} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Purchase Cost <span className="text-danger">*</span></label>
            <input type="number" min="0" name="purchaseCost" className={`form-control ${errors.purchaseCost ? "is-invalid" : ""}`} value={formData.purchaseCost} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Vendor</label>
            <select name="vendorId" className="form-select" value={formData.vendorId} onChange={handleChange}>
              <option value="">Select vendor</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Vendor Name Override</label>
            <input name="vendorName" className="form-control" value={formData.vendorName} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">PO Number</label>
            <input name="poNumber" className="form-control" value={formData.poNumber} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">GRN Number</label>
            <input name="grnNumber" className="form-control" value={formData.grnNumber} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Warranty Start <span className="text-danger">*</span></label>
            <input type="date" name="warrantyStartDate" className={`form-control ${errors.warrantyStartDate ? "is-invalid" : ""}`} value={formData.warrantyStartDate} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Warranty End <span className="text-danger">*</span></label>
            <input type="date" name="warrantyEndDate" className={`form-control ${errors.warrantyEndDate ? "is-invalid" : ""}`} value={formData.warrantyEndDate} onChange={handleChange} />
          </div>
        </Section>

        <Section title="Ownership & Depreciation">
          <div className="col-md-3">
            <label className="form-label">Location</label>
            <select name="location" className="form-select" value={formData.location} onChange={handleChange}>
              <option value="">Select location</option>
              {LOCATIONS.map((location) => (
                <option key={location}>{location}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Department</label>
            <select name="department" className="form-select" value={formData.department} onChange={handleChange}>
              <option value="">Select department</option>
              {DEPARTMENTS.map((department) => (
                <option key={department}>{department}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Depreciation Method</label>
            <select name="depreciationMethod" className="form-select" value={formData.depreciationMethod} onChange={handleChange}>
              {DEPRECIATION_METHODS.map((method) => (
                <option key={method}>{method}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Useful Life (Years)</label>
            <input type="number" min="0" name="usefulLifeYears" className="form-control" value={formData.usefulLifeYears} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Residual Value</label>
            <input type="number" min="0" name="residualValue" className="form-control" value={formData.residualValue} onChange={handleChange} />
          </div>
          <div className="col-md-9">
            <label className="form-label">Remarks</label>
            <textarea name="remarks" className="form-control" rows={2} value={formData.remarks} onChange={handleChange} />
          </div>
        </Section>

        {dynamicFields.length > 0 && (
          <Section title={`${formData.subCategory} Details`}>
            {dynamicFields.map((field) => (
              <div className="col-md-4" key={field.name}>
                <label className="form-label">
                  {field.label}
                  {field.required && <span className="text-danger"> *</span>}
                </label>

                {/* SELECT */}
                {field.type === "select" ? (
                  <select
                    className={`form-select ${errors[`category_${field.name}`] ? "is-invalid" : ""}`}
                    value={categoryFields[field.name] || ""}
                    onChange={(e) => setCategoryFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  >
                    <option value="">Select</option>
                    {(field.options || []).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>

                  /* RADIO */
                ) : field.type === "radio" ? (
                  <div className={`border rounded p-2 ${errors[`category_${field.name}`] ? "border-danger" : ""}`}>
                    {(field.options || []).map((option) => (
                      <div className="form-check" key={option}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name={`radio_${field.name}`}
                          id={`radio_${field.name}_${option}`}
                          value={option}
                          checked={categoryFields[field.name] === option}
                          onChange={(e) => setCategoryFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        />
                        <label className="form-check-label" htmlFor={`radio_${field.name}_${option}`}>
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>


                  /* CHECKBOX */
                ) : field.type === "checkbox" ? (
                  <div className="form-check mt-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`chk_${field.name}`}
                      checked={!!categoryFields[field.name]}
                      onChange={(e) =>
                        setCategoryFields((prev) => ({ ...prev, [field.name]: e.target.checked }))
                      }
                    />
                    <label className="form-check-label text-muted" htmlFor={`chk_${field.name}`}>
                      Yes
                    </label>
                  </div>

                  /* TEXTAREA */
                ) : field.type === "textarea" ? (
                  <textarea
                    className={`form-control ${errors[`category_${field.name}`] ? "is-invalid" : ""}`}
                    rows={2}
                    value={categoryFields[field.name] || ""}
                    onChange={(e) => setCategoryFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  />

                  /* ALL OTHERS: text, number, date, email, phone, url, currency, percentage, datetime-local, time, file */
                ) : (
                  <input
                    type={field.type === "currency" || field.type === "percentage" ? "number" : (field.type || "text")}
                    min={field.type === "currency" || field.type === "percentage" || field.type === "number" ? "0" : undefined}
                    step={field.type === "currency" ? "0.01" : field.type === "percentage" ? "0.1" : undefined}
                    className={`form-control ${errors[`category_${field.name}`] ? "is-invalid" : ""}`}
                    value={categoryFields[field.name] || ""}
                    onChange={(e) => setCategoryFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  />
                )}

                {errors[`category_${field.name}`] && (
                  <div className="invalid-feedback d-block">{errors[`category_${field.name}`]}</div>
                )}
              </div>
            ))}
          </Section>
        )}

        {/* ── Tabs ── */}
        <ul className="nav nav-tabs mb-0 mt-4" style={{ backgroundColor: navbarColor }}>
            {[
                ["allocations", "Allocation History"],
                ["maintenance", "Maintenance Log"],
                ["financial",   "Financial"],
                ["system",      "Audit Trail"],
                ["systemNotes", "System Notes"],
            ].map(([key, label]) => (
                <li className="nav-item" key={key}>
                    <button type="button"
                        className={`nav-link ${activeTab === key ? "active" : ""}`}
                        onClick={() => handleTabChange(key)}
                        style={{ color: activeTab === key ? navbarColor : "#fff", backgroundColor: activeTab === key ? "#fff" : "transparent", fontWeight: activeTab === key ? 600 : "normal" }}>
                        {label}
                    </button>
                </li>
            ))}
        </ul>

        <div className="p-3 border border-top-0 bg-white mb-4">
            {/* Allocation History Tab */}
            {activeTab === "allocations" && (
                <div>
                    <h6 className="mb-3">Allocation History</h6>
                    {allocations.length === 0
                        ? <p className="text-muted">No allocation records found.</p>
                        : <table className="table table-sm align-middle text-start">
                            <thead className="table-light">
                                <tr>
                                    <th>Employee</th><th>Type</th><th>Allocated On</th><th>Returned On</th><th>Condition at Issue</th><th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allocations.map((a, i) => (
                                    <tr key={i}>
                                        <td>{a.employeeName}</td>
                                        <td>{a.allocationType}</td>
                                        <td>{a.allocationDate}</td>
                                        <td>{a.returnDate || <span className="text-muted">Active</span>}</td>
                                        <td>{a.conditionAtIssue}</td>
                                        <td><span className={`badge ${a.status === "ACTIVE" ? "bg-success" : "bg-secondary"}`}>{a.status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    }
                </div>
            )}

            {/* Maintenance Tab */}
            {activeTab === "maintenance" && (
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Maintenance / Service Log</h6>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => navigate("/newmaintenance")}>
                            <i className="bi bi-plus me-1"></i> Raise Service Request
                        </button>
                    </div>
                    {maintenance.length === 0
                        ? <p className="text-muted">No maintenance records found.</p>
                        : <table className="table table-sm align-middle text-start">
                            <thead className="table-light">
                                <tr>
                                    <th>Type</th><th>Issue</th><th>Service Date</th><th>Vendor</th><th>Cost (INR)</th><th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {maintenance.map((m, i) => (
                                    <tr key={i}>
                                        <td>{m.maintenanceType}</td>
                                        <td style={{ maxWidth: 200 }}><small>{m.issueDescription}</small></td>
                                        <td>{m.serviceDate}</td>
                                        <td>{m.vendorName}</td>
                                        <td>{m.repairCost ? `₹${Number(m.repairCost).toLocaleString()}` : "—"}</td>
                                        <td><span className={`badge ${m.status === "CLOSED" ? "bg-success" : m.status === "IN_PROGRESS" ? "bg-warning text-dark" : "bg-secondary"}`}>{m.status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    }
                </div>
            )}

            {/* Financial Tab */}
            {activeTab === "financial" && assetDetails && (
                <>
                    <InfoSection title="Depreciation & Valuation" icon="bi-graph-down">
                        <Field label="Depreciation Method" value={assetDetails.depreciationMethod} />
                        <Field label="Useful Life (Years)" value={assetDetails.usefulLifeYears} />
                        <Field label="Purchase Cost (INR)" value={assetDetails.purchaseCost ? `₹${Number(assetDetails.purchaseCost).toLocaleString("en-IN")}` : null} />
                        <Field label="Residual Value (INR)" value={assetDetails.residualValue ? `₹${Number(assetDetails.residualValue).toLocaleString("en-IN")}` : null} />
                        <Field label="Current Book Value (INR)" value={assetDetails.currentBookValue ? `₹${Number(assetDetails.currentBookValue).toLocaleString("en-IN")}` : "—"} />
                    </InfoSection>
                </>
            )}

            {/* System Information Tab */}
            {activeTab === "system" && assetDetails && (
                <>
                    <InfoSection title="Record Metadata" icon="bi-info-circle">
                        <Field label="Asset Code" value={assetDetails.assetCode} />
                        <Field label="Created At" value={assetDetails.createdAt ? new Date(assetDetails.createdAt).toLocaleString() : "—"} />
                        <Field label="Last Updated" value={assetDetails.updatedAt ? new Date(assetDetails.updatedAt).toLocaleString() : "—"} />
                        <Field label="Current Status" value={assetDetails.status} badge badgeClass={STATUS_BADGE[assetDetails.status]} />
                        {assetDetails.remarks && (
                            <div className="col-12 mb-3">
                                <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Remarks</div>
                                <div style={{ fontSize: 14, color: "#374151", background: "#f9fafb", padding: "8px 12px", borderRadius: 6, border: "1px solid #e5e7eb" }}>
                                    {assetDetails.remarks}
                                </div>
                            </div>
                        )}
                        {assetDetails.categorySpecificDetails && Object.keys(assetDetails.categorySpecificDetails).length > 0 && (
                            <div className="col-12 mb-3">
                                <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Category-Specific Details</div>
                                <div className="row g-2">
                                    {Object.entries(assetDetails.categorySpecificDetails).map(([k, v]) => (
                                        <div className="col-md-3" key={k}>
                                            <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "capitalize" }}>{k.replace(/_/g, " ")}</div>
                                            <div style={{ fontSize: 14, fontWeight: 500 }}>{String(v) || "—"}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </InfoSection>

                    <AuditSection count={auditTrail.length}>
                        {auditTrail.length === 0 ? (
                            <p className="text-muted mb-0">No events recorded yet.</p>
                        ) : (
                            <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
                                <div style={{ position: "relative" }}>
                                    <div style={{ position: "absolute", left: 19, top: 0, bottom: 0, width: 2, background: "#e5e7eb" }}></div>
                                    {auditTrail.map((e, i) => {
                                        const cfg = EVENT_CONFIG[e.eventType] || { icon: "bi-circle", color: "#6b7280", bg: "#f3f4f6" };
                                        return (
                                            <div key={e.id || i} className="d-flex gap-3 mb-3" style={{ position: "relative" }}>
                                                <div className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle"
                                                    style={{ width: 38, height: 38, background: cfg.bg, color: cfg.color, border: `2px solid ${cfg.color}`, zIndex: 1 }}>
                                                    <i className={`bi ${cfg.icon}`} style={{ fontSize: 15 }}></i>
                                                </div>
                                                <div className="flex-grow-1 pt-1">
                                                    <div className="d-flex align-items-center gap-2 mb-1">
                                                        <span className="fw-semibold" style={{ fontSize: 14 }}>{EVENT_LABELS[e.eventType] || e.eventType}</span>
                                                        <span style={{ fontSize: 11, color: "#9ca3af" }}>
                                                            {e.eventTime ? new Date(e.eventTime).toLocaleString() : ""}
                                                        </span>
                                                    </div>
                                                    {e.details && (
                                                        <div style={{ fontSize: 13, color: "#4b5563" }}>{e.details}</div>
                                                    )}
                                                    {e.actor && (
                                                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                                                            <i className="bi bi-person me-1"></i>{e.actor}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </AuditSection>
                </>
            )}

            {/* System Notes */}
            {visitedTabs.has("systemNotes") && (
                <div style={{ display: activeTab === "systemNotes" ? "block" : "none" }}>
                    <div className="text-muted small mb-2">
                        <strong>Date Created:</strong>{" "}
                        {assetDetails?.createdAt ? new Date(assetDetails.createdAt).toLocaleString() : "—"}
                    </div>
                    <SystemNotes entityType="ASSET" entityId={id} token={token} />
                </div>
            )}
        </div>

        <div className="d-flex gap-2 mt-2 mb-4">
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Saving..." : "Save "}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
